import type { ToastStore } from '../stores/toast.svelte';
import { mergeRecents, rememberPick, suggestionKey, type Suggestion } from './recents';

/** Delay before hiding the dropdown on blur, so a suggestion click still fires. */
export const BLUR_CLICK_GRACE_MS = 150;
/** Debounce window between keystrokes and the network fetch. */
const REFRESH_DEBOUNCE_MS = 120;

/**
 * One dropdown row. `pick` is the shape the consumer wants back on selection
 * (an `Endpoint` for the routing input, a `HafasStopPick` for the departures
 * input); `recent` is the shape stored in the recents cache.
 */
export interface AutocompleteView<TPick> {
  key: string;
  label: string;
  badge: string;
  pick: TPick;
  recent: Suggestion;
}

/** What `Autocomplete.pick(...)` and `onKeydown(...)` return — carries the recent so callers can pull a coord. */
export interface PickResult<TPick> {
  pick: TPick;
  label: string;
  recent: Suggestion;
}

/** Options for {@link Autocomplete} — fetcher + result/recent adapters. */
export interface AutocompleteOptions<TResult, TPick> {
  /**
   * Recents-store scope this input persists into — evaluated lazily on each
   * read so reactive changes flow through. Pass `() => 'dep'` for a stable
   * scope, or `() => scopeProp` to track a component prop.
   */
  scope: () => string;
  /** Runs the network query; must respect the abort signal. */
  fetcher: (query: string, signal: AbortSignal) => Promise<TResult[]>;
  /** Adapts a fetched result into a dropdown row. */
  toView: (r: TResult) => AutocompleteView<TPick>;
  /** Adapts a persisted recent into a dropdown row, or undefined to drop it. */
  recentToView: (r: Suggestion) => AutocompleteView<TPick> | undefined;
  /** Optional toast store; when provided, non-abort fetcher rejections surface via `showError`. */
  toast?: ToastStore;
  /** Prefix used with the toast — e.g. "Address lookup". Ignored when `toast` is absent. */
  toastPrefix?: string;
  /**
   * Optional synthesizer. When provided, its return value (if any) is prepended
   * to the dropdown ahead of recents and fetched results — the primary use is
   * turning a typed `"lat,lon"` into a synthetic POSITION suggestion.
   */
  synthesizePick?: (query: string) => AutocompleteView<TPick> | undefined;
}

/**
 * Reactive autocomplete state machine, shared by every input on the page.
 * Owns the debounce timer, blur-close timer, in-flight fetch controller, and
 * the keyboard-nav index — leaving Svelte components as thin bindings that
 * only wire the DOM events to methods on this class.
 */
export class Autocomplete<TResult, TPick> {
  readonly #options: AutocompleteOptions<TResult, TPick>;
  #inflight: AbortController | undefined = undefined;
  #debounceTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  #blurTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  #disposed = false;
  open = $state(false);
  items = $state<AutocompleteView<TPick>[]>([]);
  activeIndex = $state(-1);

  constructor(options: AutocompleteOptions<TResult, TPick>) {
    this.#options = options;
  }

  #buildItems(
    fetchedViews: AutocompleteView<TPick>[],
    merged: readonly Suggestion[],
  ): AutocompleteView<TPick>[] {
    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- throwaway local Map for lookup; not reactive state
    const byKey = new Map(fetchedViews.map((view) => [suggestionKey(view.recent), view]));
    const nextItems: AutocompleteView<TPick>[] = [];
    for (const suggestion of merged) {
      const view = byKey.get(suggestionKey(suggestion)) ?? this.#options.recentToView(suggestion);
      if (view !== undefined) nextItems.push(view);
    }
    return nextItems;
  }

  #handleEnter(event: KeyboardEvent): PickResult<TPick> | undefined {
    if (this.open && this.activeIndex >= 0 && this.items.length > 0) {
      event.preventDefault();
      return this.pick(this.activeIndex);
    }
    return undefined;
  }

  #handleNav(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % this.items.length;
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + this.items.length) % this.items.length;
      return;
    }
    if (event.key === 'Escape') this.open = false;
  }

  /**
   * Runs the fetcher, aborting any predecessor. Empty query → `[]`.
   * `AbortError` (raised when a newer fetch supersedes this one) is swallowed;
   * every other rejection is forwarded to `options.toast?.showError(...)` and
   * also returns `[]` so the dropdown falls back to whatever recents match.
   */
  async fetchResults(query: string): Promise<TResult[]> {
    if (query.trim().length === 0) return [];
    this.#inflight?.abort();
    const controller = new AbortController();
    this.#inflight = controller;
    try {
      return await this.#options.fetcher(query, controller.signal);
    } catch (error: unknown) {
      this.#options.toast?.showError(this.#options.toastPrefix ?? 'Search', error);
      return [];
    } finally {
      /* istanbul ignore else -- else fires only when a newer fetch already claimed inflight; harmless no-op */
      if (this.#inflight === controller) this.#inflight = undefined;
    }
  }

  /**
   * Fetches for `query`, merges with recents, replaces `items`, and resets
   * `activeIndex` to `0` (`-1` when the result is empty). No-op after {@link dispose}.
   */
  async refresh(query: string): Promise<void> {
    const fetched = await this.fetchResults(query);
    if (this.#disposed) return;
    const fetchedViews = fetched.map((rawResult) => this.#options.toView(rawResult));
    const fetchedSuggestions = fetchedViews.map((view) => view.recent);
    const merged = mergeRecents(this.#options.scope(), query, fetchedSuggestions);
    const combined = this.#buildItems(fetchedViews, merged);
    const synthetic = this.#options.synthesizePick?.(query);
    this.items = synthetic ? [synthetic, ...combined] : combined;
    this.activeIndex = this.items.length > 0 ? 0 : -1;
  }

  /** Schedules a debounced {@link refresh}. */
  scheduleRefresh(query: string): void {
    if (this.#debounceTimer !== undefined) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = undefined;
      void this.refresh(query);
    }, REFRESH_DEBOUNCE_MS);
  }

  /** Opens the dropdown and refreshes with the current text. */
  onFocus(value: string): void {
    this.open = true;
    void this.refresh(value);
  }

  /**
   * Schedules `open = false` after {@link BLUR_CLICK_GRACE_MS} so a suggestion
   * click still fires; supersedes any pending grace timer.
   */
  onBlur(): void {
    clearTimeout(this.#blurTimer);
    this.#blurTimer = setTimeout(() => {
      this.open = false;
      this.#blurTimer = undefined;
    }, BLUR_CLICK_GRACE_MS);
  }

  /**
   * Handles keyboard nav. Enter with a highlighted row picks it and returns
   * `{pick, label}`; arrows mutate `activeIndex`; Escape closes the dropdown;
   * every other key (and Enter with nothing highlighted) returns `undefined`.
   */
  onKeydown(event: KeyboardEvent): PickResult<TPick> | undefined {
    if (event.key === 'Enter') return this.#handleEnter(event);
    if (!this.open || this.items.length === 0) return undefined;
    this.#handleNav(event);
    return undefined;
  }

  /**
   * Commits `items[index]` to recents, closes the dropdown, and returns its
   * `{pick, label}`. Throws `TypeError` when `index` is out of range.
   */
  pick(index: number): PickResult<TPick> {
    const item = this.items[index];
    rememberPick(this.#options.scope(), item.recent);
    this.open = false;
    return { pick: item.pick, label: item.label, recent: item.recent };
  }

  /**
   * Sets `open = true` and schedules a debounced ({@link REFRESH_DEBOUNCE_MS})
   * {@link refresh} for `value`; earlier pending schedules are cancelled.
   */
  handleInput(value: string): void {
    this.open = true;
    this.scheduleRefresh(value);
  }

  /** Sets `activeIndex` — bound to the dropdown's hover event. */
  setActive(index: number): void {
    this.activeIndex = index;
  }

  /**
   * Cancels timers, aborts the in-flight fetch, and makes future {@link refresh}
   * completions no-op. Call on unmount; do not call other methods after — the
   * class does not guard every entrypoint against re-scheduling work.
   */
  dispose(): void {
    this.#disposed = true;
    if (this.#debounceTimer !== undefined) clearTimeout(this.#debounceTimer);
    if (this.#blurTimer !== undefined) clearTimeout(this.#blurTimer);
    this.#inflight?.abort();
  }
}
